import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, IconButton, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Passenger } from '../../../data/models/passenger.model';
import PassengerNamedLocationsList from './PassengerNamedLocationsList';
import PassengerNamedLocationsDialog from './PassengerNamedLocationsDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PassengerNamedLocations';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
  },

  [`& .${classes.details}`]: {
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
}));

interface PassengerNamedLocationProps {
  passenger: Passenger | null;
}

export default function PassengerNamedLocations({
  passenger,
}: PassengerNamedLocationProps) {
  const t = useCommonTranslation();
  const [openAddDialog, setOpenAddDialog] = React.useState(false);
  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <>
      <StyledBox
        className={classes.container}
        data-testid="passenger-named-locations-container">
        <Paper
          elevation={2}
          className={classes.details}
          data-testid="passenger-named-locations-section">
          <Box
            className={classes.header}
            data-testid="passenger-named-locations-header">
            <Typography variant="h3">
              {t('passenger.namedLocations')}
            </Typography>
            {!isSuperReaderUser && (
              <Tooltip title={t('passenger.addNamedLocation')}>
                <span>
                  <IconButton
                    onClick={() => setOpenAddDialog(true)}
                    color="primary"
                    size="small">
                    <AddCircleOutlineOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <PassengerNamedLocationsList passenger={passenger} />
        </Paper>
      </StyledBox>
      <PassengerNamedLocationsDialog
        passenger={passenger}
        open={openAddDialog}
        close={() => setOpenAddDialog(false)}
      />
    </>
  );
}
